import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, Button, Input } from '../components';
import { MapPin, Wifi, UserCheck, Ban } from 'lucide-react';
import toast from 'react-hot-toast';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import type { EmployerLocation } from '../components/EmployerOrgStructuur';
import api from '../services/api';
import {
  fetchTrajectDisciplines,
  fetchExperts,
  maakProjectAanvraag,
  type ExpertOverzicht,
  type PoolStatus,
} from '../services/projectenApi';

/**
 * [➕ Start Nieuw Organisatie Project] — stap 1 van het projecttraject.
 *
 * HR omschrijft de organisatievraag, kiest de discipline en voert de
 * bedrijfspostcode in. De Directe Selectie-schil toont daarna alle experts in
 * de discipline met hun realtime status; HR selecteert zelf de professional.
 * De reisradius- en capaciteits-grendel draaien server-side nogmaals bij het
 * indienen van de aanvraag.
 */

const STATUS_BOL: Record<PoolStatus, { kleur: string; label: string }> = {
  GROEN: { kleur: 'bg-green-500', label: 'Beschikbaar voor nieuwe projecten' },
  BLAUW: { kleur: 'bg-blue-500', label: 'Beschikbaar — alleen online' },
  ORANJE: { kleur: 'bg-orange-400', label: 'Met verlof' },
  ROOD: { kleur: 'bg-red-500', label: 'Vol' },
};

const EmployerProjectCreatePage: React.FC = () => {
  useCockpitTitle('Start Nieuw Organisatie Project', 'Gerichte expert-selectie met realtime beschikbaarheidscheck');
  const navigate = useNavigate();
  const { t } = useTranslation('employer');

  const [titel, setTitel] = React.useState('');
  const [omschrijving, setOmschrijving] = React.useState('');
  const [discipline, setDiscipline] = React.useState('');
  const [postcode, setPostcode] = React.useState('');
  // null = nog niet gekozen; '' = bewust geen vestiging.
  const [gekozenLocatieId, setGekozenLocatieId] = React.useState<string | null>(null);
  const [zoekActief, setZoekActief] = React.useState(false);
  const [alternatieven, setAlternatieven] = React.useState<ExpertOverzicht[] | null>(null);

  const { data: disciplines } = useQuery({
    queryKey: ['traject-disciplines'],
    queryFn: async () => (await fetchTrajectDisciplines()).data?.data as Array<{ professional_type: string; label: string }>,
  });

  // Zelfde aanroep/cache als EmployerOrgStructuur; backend levert alleen actieve
  // vestigingen, hoofdvestiging bovenaan.
  const { data: locaties = [] } = useQuery({
    queryKey: ['employer-locations'],
    queryFn: async () => (await api.get('/employer/locations')).data?.data as EmployerLocation[],
  });

  // Precies één vestiging → die is voorgeselecteerd zolang HR niets anders kiest.
  const locatieId = gekozenLocatieId ?? (locaties.length === 1 ? locaties[0].id : '');
  const locatie = locaties.find((l) => l.id === locatieId);
  const locatiePostcode = locatie?.postcode?.trim() || '';

  // Backend: postcode van de gekozen vestiging gaat vóór de ingevulde postcode.
  const effectievePostcode = locatiePostcode || postcode.trim();
  const postcodeGeldig = /^[1-9][0-9]{3}\s?[A-Za-z]{2}$/.test(effectievePostcode);
  const formulierCompleet = titel.trim().length >= 3 && discipline && postcodeGeldig;

  const { data: experts, isFetching } = useQuery({
    queryKey: ['traject-experts', discipline, effectievePostcode],
    queryFn: async () => (await fetchExperts(discipline, effectievePostcode)).data?.data as ExpertOverzicht[],
    enabled: zoekActief && Boolean(formulierCompleet),
  });

  const aanvraagMutation = useMutation({
    mutationFn: (professionalId: string) => {
      const payload: Parameters<typeof maakProjectAanvraag>[0] & { location_id?: string } = {
        professional_id: professionalId,
        discipline,
        titel: titel.trim(),
        omschrijving: omschrijving.trim() || undefined,
        postcode: postcode.trim(),
        location_id: locatieId || undefined,
      };
      return maakProjectAanvraag(payload);
    },
    onSuccess: () => {
      toast.success('Aanvraag verstuurd — de professional ontvangt direct een signaal in de cockpit.');
      navigate('/employer/projecten');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'De aanvraag is niet gelukt.');
      // De grendel adviseert bij blokkade back-up experts uit de regio-matrix.
      if (err.response?.data?.alternatieven?.length) {
        setAlternatieven(err.response.data.alternatieven);
      }
    },
  });

  const ExpertKaart: React.FC<{ expert: ExpertOverzicht; backup?: boolean }> = ({ expert, backup }) => {
    const bol = STATUS_BOL[expert.pool_status];
    const selecteerbaar =
      (expert.pool_status === 'GROEN' || expert.pool_status === 'BLAUW') &&
      expert.binnen_radius &&
      expert.capaciteit_beschikbaar;

    return (
      <div className={`border rounded-lg p-4 ${selecteerbaar ? 'border-gray-200' : 'border-gray-200 bg-gray-50 opacity-75'}`}>
        <div className="flex items-center justify-between gap-2 mb-2">
          <div>
            <p className="font-semibold text-gray-900">{expert.bedrijfsnaam || expert.naam}</p>
            {expert.bedrijfsnaam && <p className="text-xs text-gray-500">{expert.naam}</p>}
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-700">
            <span className={`w-3 h-3 rounded-full ${bol.kleur}`} />
            {bol.label}
          </span>
        </div>
        <div className="flex flex-wrap gap-3 text-xs text-gray-600 mb-3">
          {expert.werkt_alleen_online ? (
            <span className="inline-flex items-center gap-1"><Wifi className="w-3.5 h-3.5" /> Alleen online</span>
          ) : (
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />
              {expert.afstand_km != null ? `${expert.afstand_km} km` : 'afstand onbekend'}
              {expert.reisradius_km != null && ` (radius ${expert.reisradius_km} km)`}
            </span>
          )}
          {!expert.binnen_radius && (
            <span className="inline-flex items-center gap-1 text-red-600"><Ban className="w-3.5 h-3.5" /> Buiten reisradius</span>
          )}
          {!expert.capaciteit_beschikbaar && (
            <span className="inline-flex items-center gap-1 text-red-600"><Ban className="w-3.5 h-3.5" /> Capaciteit vol</span>
          )}
        </div>
        <Button
          size="sm"
          disabled={!selecteerbaar}
          loading={aanvraagMutation.isPending}
          onClick={() => aanvraagMutation.mutate(expert.professional_id)}
        >
          <UserCheck className="w-4 h-4 mr-1" />
          {backup ? 'Selecteer deze back-up expert' : 'Selecteer deze expert'}
        </Button>
      </div>
    );
  };

  return (
    <div className="max-w-4xl">
      <Card>
        <div className="p-6 space-y-4">
          <Input
            label="Organisatievraag (projecttitel)"
            placeholder='Bijv. "Wettelijke RI&E-toetsing en afdelingsscan fabriekshal"'
            value={titel}
            onChange={(e) => setTitel(e.target.value)}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Omschrijving</label>
            <textarea
              className="w-full border border-gray-300 rounded-md p-2 text-sm"
              rows={3}
              value={omschrijving}
              onChange={(e) => setOmschrijving(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gewenste discipline</label>
              <select
                className="w-full border border-gray-300 rounded-md p-2 text-sm"
                value={discipline}
                onChange={(e) => { setDiscipline(e.target.value); setZoekActief(false); }}
              >
                <option value="">— Kies discipline —</option>
                {(disciplines || []).map((d) => (
                  <option key={d.professional_type} value={d.professional_type}>{d.label}</option>
                ))}
              </select>
            </div>
            {locaties.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('projectCreate.vestiging')}</label>
                <select
                  className="w-full border border-gray-300 rounded-md p-2 text-sm"
                  value={locatieId}
                  onChange={(e) => { setGekozenLocatieId(e.target.value); setZoekActief(false); }}
                >
                  <option value="">{t('projectCreate.geenVestiging')}</option>
                  {locaties.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.naam}
                      {l.postcode ? ` — ${l.postcode}` : ''}
                      {l.is_hoofdvestiging ? ` (${t('projectCreate.hoofdvestiging')})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <Input
                label={locatiePostcode ? t('projectCreate.postcodeOptioneel') : 'Bedrijfspostcode'}
                placeholder={locatiePostcode || '1234AB'}
                value={postcode}
                onChange={(e) => { setPostcode(e.target.value); setZoekActief(false); }}
              />
              {locatiePostcode && (
                <p className="mt-1 text-xs text-gray-500">
                  {t('projectCreate.postcodeVanVestiging', { postcode: locatiePostcode })}
                </p>
              )}
            </div>
          </div>
          <Button disabled={!formulierCompleet} onClick={() => { setAlternatieven(null); setZoekActief(true); }}>
            Toon beschikbare experts
          </Button>
        </div>
      </Card>

      {zoekActief && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Directe selectie — experts in deze discipline</h2>
          {isFetching ? (
            <p className="text-gray-600">Realtime status en afstanden ophalen…</p>
          ) : !experts || experts.length === 0 ? (
            <Card><p className="p-6 text-gray-600">Geen professionals gevonden binnen deze discipline.</p></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {experts.map((e) => <ExpertKaart key={e.professional_id} expert={e} />)}
            </div>
          )}
        </div>
      )}

      {alternatieven && alternatieven.length > 0 && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold text-red-700 mb-3">
            Gekozen expert niet beschikbaar — beschikbare back-up experts uit de regio-matrix
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {alternatieven.map((e) => <ExpertKaart key={e.professional_id} expert={e} backup />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployerProjectCreatePage;
